import type { User } from '@auth0/auth0-spa-js';
import { Auth0Client, GenericError } from '@auth0/auth0-spa-js';

import { env } from '@packages/utils/env';

// using Auth0Client instead of createAuth0Client:
// - createAuth0Client is async and forces you to await before using the client and runs checkSession
// - Auth0Client doesn't await and can synchronously reference the client after module loads
// - Auth0Client allows to control when/if login occurs which is beneficial for routing logic in loaders
const client = new Auth0Client({
  authorizationParams: {
    audience: env.VITE_RTK_AUTH0_AUDIENCE,
    redirect_uri: window.location.origin + '/callback',
  },
  clientId: env.VITE_RTK_AUTH0_CLIENT_ID,
  domain: env.VITE_RTK_AUTH0_DOMAIN,
  cacheLocation: 'memory', // avoid using localStorage to prevent potential XSS attacks
  useRefreshTokens: true,
  // falls back to using an iframe to `/authorize` if no refresh token is available
  // this prevents missing_refresh_token and invalid_grant errors
  useRefreshTokensFallback: true,
});

const logout = async () => {
  await client.logout({
    logoutParams: {
      returnTo: window.location.origin,
    },
  });

  // Wait for the user to be redirected by preventing function from resolving.
  return new Promise(() => undefined);
};

const isAuthCallback = (query = window.location.search) => {
  const searchParams = new URLSearchParams(query);

  return (
    (searchParams.has('code') && searchParams.has('state')) ||
    searchParams.has('error')
  );
};

const authRedirectCallback = async (): Promise<string | undefined> => {
  if (env.MODE === 'e2e') return '/';

  if (isAuthCallback()) {
    const { appState } = await client.handleRedirectCallback();

    // returnTo needs to be returned so that the router can handle the redirect in the app
    return appState.returnTo
      ? `${appState.returnTo}${
          appState.locationHash ? appState.locationHash : ''
        }`
      : '/';
  }
};

const login = async () => {
  if (env.MODE === 'e2e') return undefined;

  await client.loginWithRedirect({
    appState: {
      locationHash: window.location.hash,
      returnTo: window.location.pathname + window.location.search,
    },
  });

  // The promise returned by loginWithRedirect only indicates that the redirect has been initiated successfully.
  // Wait for the user to be redirected by preventing function from resolving.
  return new Promise(() => undefined);
};

const verifyUser: () => Promise<null | undefined> = async () => {
  if (env.MODE === 'e2e') return null;

  const isUserAuthenticated = await client.isAuthenticated();

  if (isUserAuthenticated) {
    // prefetch token in the background to handle page load / new tabs for memory cache
    client.getTokenSilently().catch(() => {
      // do nothing
    });

    return null;
  }

  await login();
};

const getTokenSilently = async () => {
  try {
    const token = await client.getTokenSilently();
    return token;
  } catch (err) {
    if (
      // login_required: returned if fallback mechanism fails
      // mfa_required: thrown when the token exchange results in a `mfa_required` error
      err instanceof GenericError &&
      (err.error === 'login_required' || err.error === 'mfa_required')
    ) {
      // send user to get re-authorized
      await login();
    } else {
      // log user out and force them to re-enter the login flow
      await logout();
    }
  }
};

const getToken = async () => {
  if (env.MODE === 'e2e') return 'E2E Token';

  const isUserAuthenticated = await client.isAuthenticated();

  if (isUserAuthenticated) {
    return await getTokenSilently();
  } else {
    await login();
  }
};

const fetchWithToken = async (
  url: RequestInfo | URL,
  options?: RequestInit,
) => {
  const token = await getToken();

  if (!token) {
    throw new Error('Missing access token');
  }

  const headers = new Headers(options?.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(url, { ...options, headers });
};

const getIdTokenClaims = async () => {
  if (env.MODE === 'e2e')
    return { __raw: 'E2E Id Token', email: 'user@e2e.com' };

  return await client.getIdTokenClaims();
};

const getUser = async () => {
  if (env.MODE === 'e2e')
    return { name: 'E2E User', email: 'user@e2e.com' } satisfies User;

  return await client.getUser();
};

export {
  authRedirectCallback,
  getIdTokenClaims,
  getToken,
  getUser,
  fetchWithToken,
  login,
  logout,
  verifyUser,
};
